import { useContext, useEffect, type ReactNode } from 'react'
import { useAuth } from '../../context/useAuth'
import { WorkspaceContext } from '../../context/workspaceContextCore'
import { isHelperOnlyAccount } from '../../utils/helperMembership'
import { useIsFamilyOnly } from '../../hooks/useFamilyAccess'
import { resolveFamilyConsoleRedirect } from '../../utils/familyRedirect'
import { useNls } from '../../nls/nlsProvider'
import './appRouteMessage.css'

type MemberRoleGuardProps = {
  children: ReactNode
}

const redirect = (path: string, options?: { reload?: boolean }) => {
  if (typeof window === 'undefined') return
  if (window.location.pathname === path) return
  if (options?.reload) {
    window.location.replace(path)
    return
  }
  window.history.replaceState(null, '', path)
  window.dispatchEvent(new PopStateEvent('popstate'))
}

export const MemberRoleGuard = ({ children }: MemberRoleGuardProps) => {
  const { currentUser, userProfile, isAuthLoading } = useAuth()
  const workspaceContext = useContext(WorkspaceContext)
  const isHelperUser = isHelperOnlyAccount({
    userProfile,
    currentUser,
    workspaces: workspaceContext?.workspaces,
    rolesByWorkspaceId: workspaceContext?.rolesByWorkspaceId,
  })
  const isFamilyOnly = useIsFamilyOnly()
  const { t } = useNls()

  const familyRedirectPath = resolveFamilyConsoleRedirect({
    isAuthenticated: Boolean(currentUser),
    isHelper: isHelperUser,
    isFamilyOnly,
    activeRole: workspaceContext?.activeRole,
    topLevelRouteKind: 'console',
  })

  useEffect(() => {
    if (isAuthLoading) return
    if (!currentUser) {
      redirect('/')
      return
    }
    if (isHelperUser) {
      redirect('/helper/dashboard', { reload: true })
      return
    }
    if (familyRedirectPath) {
      redirect(familyRedirectPath)
      return
    }
  }, [currentUser, familyRedirectPath, isAuthLoading, isHelperUser])

  if (isAuthLoading || !currentUser || !userProfile) {
    return (
      <main className="appRouteMessage">
        Checking member access...
      </main>
    )
  }

  if (isHelperUser) {
    return (
      <main className="appRouteMessage">
        Opening Helper...
      </main>
    )
  }

  if (familyRedirectPath) {
    return (
      <main className="appRouteMessage">
        {t('NLSMemberOpeningFamily')}
      </main>
    )
  }

  return <>{children}</>
}


